import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axiosUserInstance from '../../../axiosUserInstance';
import Loader from '../../../Components/Loader/Loader';
import EventCard from './EventCard';
import './Events.css';
import omfLogo from '../../../assets/omfLogo.svg';
import { useAuth } from '../../../AuthProvider';

export default function Events({ embed = false }) {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(0);
    const { userID } = useAuth();

    // Parse date strings safely: treat YYYY-MM-DD as a local date
    const parseDateLocal = (s) => {
        if (!s) return null;
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
            const [y, m, d] = s.split('-').map(Number);
            return new Date(y, m - 1, d);
        }
        const dte = new Date(s);
        return Number.isNaN(dte?.getTime?.()) ? null : dte;
    };

    useEffect(() => {
        let mounted = true;
        const fetchEvents = async () => {
            try {
                setLoading(true);
                setError('');
                const res = await axiosUserInstance.get('/events/events', { skipAuth: true });
                const raw = res?.data?.events || [];
                const now = new Date();
                const list = (raw || []).filter((ev) => {
                    const dt = parseDateLocal(ev?.event_datetime);
                    if (!dt) return false; // skip if invalid/missing
                    // keep same-day future or today events
                    return dt.getTime() >= now.getTime();
                });
                if (!mounted) return;
                setEvents(list);
                setSelectedIndex(0);
            } catch (err) {
                if (!mounted) return;
                setError('Failed to load events');
                // eslint-disable-next-line no-console
                console.error('Events fetch error', err);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchEvents();
        return () => { mounted = false; };
    }, []);

    const featured = useMemo(() => (events?.length ? events[Math.max(0, Math.min(selectedIndex, events.length - 1))] : null), [events, selectedIndex]);

    return (
        <div className={`events-page ${embed ? 'events-page--embed' : ''}`}>
            {loading && <Loader />}
            {!embed && (
                <header className="ed-header">
                    <div className="ed-header__inner">
                        <Link to="/" className="ed-brand" aria-label="Oh My Food">
                            <img src={omfLogo} alt="Oh My Food" />
                            <span>Oh My Food</span>
                        </Link>
                        <nav className="ed-auth" aria-label="Authentication">
                            <Link to="/signup">Sign Up</Link>
                            <Link to="/login">Sign In</Link>
                        </nav>
                    </div>
                </header>
            )}
            {!embed && !userID && (
                <div className="ed-banner">
                    <div className="ed-banner__inner">
                        <span className="ed-banner__text">Sign in to save 5% on your tickets</span>
                        <div className="ed-banner__actions">
                            <Link to="/login">Sign In</Link>
                            <Link to="/signup">Sign Up</Link>
                        </div>
                    </div>
                </div>
            )}
            <div className="events-container">
                <aside className="events-flyer">
                    {featured ? (
                        <EventCard event={featured} featured to={`/events/${featured?.event_id}`} />
                    ) : (
                        <div className="events-flyer__placeholder">
                            <div className="placeholder-image" />
                            <div className="placeholder-lines">
                                <div className="line w-60" />
                                <div className="line w-40" />
                                <div className="line w-80" />
                            </div>
                        </div>
                    )}
                </aside>

                <section className={`events-list ${embed ? 'events-list--embed' : ''}`}>
                    <header className="events-header">
                        <h1 className="events-title">Events</h1>
                        {error && <span className="events-error">{error}</span>}
                    </header>

                    <div className="events-grid">
                        {events?.map((ev, idx) => (
                            <EventCard
                                key={ev.event_id || idx}
                                event={ev}
                                onClick={() => setSelectedIndex(idx)}
                                to={`/events/${ev.event_id}`}
                            />
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
}
